import { Button } from "@components/button";
import { PlusIcon, StatusLockedIcon, StatusPartlyLockedIcon, StatusUnlockedIcon } from "@components/icon";
import { formatDateToDateString } from "@components/inputs/date/utils";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { AuditTrailFieldMode, IFieldDefFn, IGetValueArgs, not } from "@components/smart/FieldInfo";
import {
    getCommonFilterDefs,
    getCommonTableColumnsDefs,
    withDisplayName
} from "@components/smart/GeneralFieldDefinition";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem, ISummaryItemEditProps } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import { TFormatterFn } from "@components/smart/smartTable/SmartTable.utils";
import { TCellValue } from "@components/table";
import { getNewItemsMaxId } from "@odata/Data.utils";
import {
    EntitySetName,
    EntityTypeName,
    FiscalPeriodEntity,
    FiscalYearEntity,
    IFiscalPeriodEntity,
    IFiscalYearStatusEntity
} from "@odata/GeneratedEntityTypes";
import { FiscalYearStatusCode } from "@odata/GeneratedEnums";
import { IFormatOptions } from "@odata/OData.utils";
import { isDefined } from "@utils/general";
import i18next from "i18next";
import React from "react";
import { DefaultTheme } from "styled-components";
import * as yup from "yup";

import Dialog from "../../components/dialog/Dialog";
import SmartField, { ISmartFieldChange } from "../../components/smart/smartField/SmartField";
import { FieldType, GroupedField, IconSize, LabelStatus, Sort, Status, TextAlign, ValidatorType } from "../../enums";
import { ColoredText } from "../../global.style";
import { TValue } from "../../global.types";
import { Model } from "../../model/Model";
import { IFilterQuery } from "../../model/TableStorage";
import { ValidationMessage } from "../../model/Validator.types";
import BindingContext, { IEntity } from "../../odata/BindingContext";
import { getUtcDateBy, getUtcDayjs } from "../../types/Date";
import { IFormDef } from "../../views/formView/Form";
import { FormStorage } from "../../views/formView/FormStorage";
import ConfirmationButtons from "../../views/table/ConfirmationButtons";
import { IChangedFilter, ISplitPageTableDef } from "../../views/table/TableView.utils";
import { setDefByEntityType } from "../getDefByEntityType";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "../PageUtils";
import {
    DefaultPeriodsPath,
    FiscalYearDefaultPeriod,
    getDefaultFiscalYearDateStart,
    getFiscalPeriodNumber,
    getFiscalYearNumbers,
    updatePeriodNumbers
} from "./FiscalYear.utils";
import FiscalYearFormView from "./FiscalYearFormView";

const yearFilterPath = BindingContext.localContext("year");

const getStatusColor = (StatusCode: string): keyof DefaultTheme => {
    switch (StatusCode) {
        case FiscalYearStatusCode.Active:
            return "C_SEM_text_good";
        case FiscalYearStatusCode.NotUsed:
            return "C_SEM_text_warning";
    }
    return null;
};

const statusFormatter: TFormatterFn = (val: TValue, args?: IFormatOptions): TCellValue => {
    const title = val?.toString() ?? "";
    const color: keyof DefaultTheme = getStatusColor(args.entity.Status?.Code);

    const colorWrapper = <ColoredText color={color}>{val}</ColoredText>;

    return { tooltip: title, value: colorWrapper };
};

export const getFiscalYearStatus = (status: FiscalYearStatusCode): Status => {
    switch (status) {
        case FiscalYearStatusCode.Active:
            return Status.Success;
        case FiscalYearStatusCode.NotUsed:
            return Status.Warning;
    }
    return null;
};

const lockFormatter = (val: TValue, values: IEntity, property: string): TCellValue => {
    let anyUnlocked = false;
    let anyLocked = false;

    if (isDefined(values[property])) { // current entity is one of the nested fiscal periods
        anyUnlocked = !values[property];
        anyLocked = values[property];
    } else { // current entity is fiscal year that averages the values from all the periods
        for (const period of values.Periods) {
            anyUnlocked = anyUnlocked || !period[property];
            anyLocked = anyLocked || period[property];

            if (anyUnlocked && anyLocked) {
                break;
            }
        }
    }

    let Icon;
    let title;

    if (anyUnlocked && !anyLocked) {
        Icon = StatusUnlockedIcon;
        title = i18next.t("FiscalYear:Table.Unlocked");
    } else if (anyLocked && anyUnlocked) {
        Icon = StatusPartlyLockedIcon;
        title = i18next.t("FiscalYear:Table.PartiallyUnlocked");
    } else {
        Icon = StatusLockedIcon;
        title = i18next.t("FiscalYear:Table.Locked");
    }

    return { tooltip: title, value: <Icon title={title} width={IconSize.S} height={IconSize.S}/> };
};

const lockedApFormatter = (val: TValue, args?: IFormatOptions): TCellValue => {
    return lockFormatter(val, args.entity, "IsLockedForAP");
};

const lockedArFormatter = (val: TValue, args?: IFormatOptions): TCellValue => {
    return lockFormatter(val, args.entity, "IsLockedForAR");
};

const periodNameFormatter = (val: TValue, args?: IFormatOptions): TCellValue => {
    return args.entity.Name;
};

const yearFormatter = (val: TValue, args?: IFormatOptions): string => {
    const startYear = args.entity.DateStart.getFullYear();
    const endYear = args.entity.DateEnd.getFullYear();

    return startYear === endYear ? startYear.toString() : `${startYear}/${endYear}`;
};

// field is editable even in active FiscalYear
const isReadOnlyLoose = (args: IGetValueArgs) => {
    return args.storage.data.entity?.StatusCode === FiscalYearStatusCode.Closed;
};

const isCustomPeriod = (args: IGetValueArgs) => {
    return args.storage.getValueByPath(DefaultPeriodsPath) === FiscalYearDefaultPeriod.Custom;
};

const fiscalPeriodYearPath = BindingContext.localContext("Year");

function getFiscalYearNumberItems(storage: FormStorage): ISelectItem[] {
    const { entity, bindingContext, additionalResults } = storage.data;
    const fiscalYearId = bindingContext.getKey();
    const otherFiscalYears = (additionalResults[1]?.value ?? [])
        .filter((fiscalYear: IEntity) => bindingContext.isNew() || fiscalYear.Id !== fiscalYearId);

    const possibleNumbers = getFiscalYearNumbers(entity.DateStart, entity.DateEnd, otherFiscalYears);

    return possibleNumbers.map(number => ({ id: number, label: number }));
}

const getLockItems = () => (
    [
        {
            label: i18next.t("FiscalYear:Table.Unlocked"),
            id: false
        },
        {
            label: i18next.t("FiscalYear:Table.Locked"),
            id: true
        }
    ]
);

const AddButtonPath = BindingContext.localContext("addButton");

const handleAddPeriod = (storage: FormStorage) => {
    const periods = [...storage.data.entity.Periods ?? []];
    const previousPeriod = periods[periods.length - 1];
    const maxNewId = getNewItemsMaxId(periods);
    const newPeriod = BindingContext.createNewEntity(maxNewId + 1);

    newPeriod.Number = getFiscalPeriodNumber(storage.data.entity.Number, periods.length);

    if (previousPeriod && previousPeriod.DateEnd) {
        newPeriod.DateStart = getUtcDayjs(previousPeriod.DateEnd).add(1, "month").startOf("month").toDate();
    }

    periods.push(newPeriod);
    storage.data.entity.Periods = periods;
    storage.refresh();
};

export const getDefinitions: IGetDefinition = (): IDefinition => {
    const table: ISplitPageTableDef = {
        filterBarDef: [{
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            defaultFilters: [
                "Number",
                "Periods/Name",
                yearFilterPath,
                "Status",
                "Periods/IsLockedForAP",
                "Periods/IsLockedForAR"
            ],
            filterDefinition: {
                Number: {
                    id: "Number", isValueHelp: false, filterName: ["", `/Periods/Number`],
                    // get around Number MaxLength 4 (Periods/Number doesn't have this restriction)
                    validator: {
                        type: ValidatorType.Custom,
                        settings: {
                            customSchema: (args: IGetValueArgs) => {
                                return yup.string()
                                    .test("required", ValidationMessage.Required,
                                        function(this: yup.TestContext, value: TValue) {
                                            return true;
                                        }).nullable();
                            }
                        }
                    }
                },
                "Periods/Name": {
                    id: "Periods/Name", isValueHelp: false, description: false
                },
                DateStart: {
                    id: "DateStart", isValueHelp: false, filterName: ["", `/Periods/DateStart`]
                },
                DateEnd: {
                    id: "DateEnd", isValueHelp: false, filterName: ["", `/Periods/DateEnd`]
                },
                [yearFilterPath]: {
                    id: yearFilterPath,
                    label: i18next.t("FiscalYear:Columns.Year"),
                    isValueHelp: false,
                    filter: {
                        buildFilter: (item: IChangedFilter): IFilterQuery | string => {
                            const year = item.value as number;

                            if (!year) {
                                return "";
                            }

                            const yearDate = getUtcDayjs(getUtcDateBy(year));
                            const yearStart = formatDateToDateString(yearDate.startOf("year"));
                            const yearEnd = formatDateToDateString(yearDate.endOf("year"));

                            const mainQuery = `DateStart ge ${yearStart} AND DateEnd le ${yearEnd}`;
                            const nestedQuery = `(${mainQuery} OR Periods/any(x: x/DateStart ge ${yearStart} AND x/DateEnd le ${yearEnd}))`;

                            return {
                                query: nestedQuery,
                                collectionQueries: {
                                    Periods: { query: mainQuery }
                                }
                            };
                        }
                    },
                    validator: {
                        type: ValidatorType.Custom,
                        settings: {
                            customSchema: () => yup.number().typeError(ValidationMessage.NotAYear).nullable().positive(ValidationMessage.NotAYear).integer(ValidationMessage.NotAYear)
                        }
                    }
                },
                ...withDisplayName("Status"),
                "Periods/IsLockedForAP": {
                    id: "Periods/IsLockedForAP",
                    description: false,
                    isValueHelp: false,
                    type: FieldType.ComboBox,
                    fieldSettings: { items: getLockItems(), noRecordText: i18next.t("Common:Select.All") }
                },
                "Periods/IsLockedForAR": {
                    id: "Periods/IsLockedForAR",
                    description: false,
                    isValueHelp: false,
                    type: FieldType.ComboBox,
                    fieldSettings: { items: getLockItems(), noRecordText: i18next.t("Common:Select.All") }
                },
                ...getCommonFilterDefs()
            },

            isValueHelp: true
        }],
        id: `${EntityTypeName.FiscalYear}Table`,
        initialSortBy: [{ id: "Number", sort: Sort.Desc }],
        columns: [
            "Number",
            "Periods/Name",
            "DateStart",
            "DateEnd",
            "Status",
            // virtual column without representation in metadata
            fiscalPeriodYearPath,
            "Periods/IsLockedForAP",
            "Periods/IsLockedForAR"
        ],
        columnDefinition: {
            Number: { id: "Number", isDisabled: true, isRequired: true },
            DateStart: { id: "DateStart" },
            DateEnd: { id: "DateEnd" },
            Status: { id: "Status", fieldSettings: { displayName: "Name" }, formatter: statusFormatter },
            ...getCommonTableColumnsDefs(),
            [fiscalPeriodYearPath]: {
                label: i18next.t("FiscalYear:Columns.Year"),
                formatter: yearFormatter,
                fieldSettings: { disableSort: true },
                additionalProperties: [{ id: "/DateStart" }, { id: "/DateEnd" }]

            },
            "Periods/Name": {
                id: "Periods/Name",
                formatter: periodNameFormatter,
                // so far, SmartTable and export don't support sort on children
                fieldSettings: {
                    disableSort: true
                }
            },
            "Periods/IsLockedForAP": {
                id: "Periods/IsLockedForAP", textAlign: TextAlign.Center, formatter: lockedApFormatter,
                // we have to add additionalProperties to some of the columns to load all the needed properties of Periods
                additionalProperties: [{
                    id: "/Periods/Name"
                }, {
                    id: "/Periods/Number"
                }, {
                    id: "/Periods/DateStart"
                }, {
                    id: "/Periods/DateEnd"
                }],
                fieldSettings: { disableSort: true }
            },
            "Periods/IsLockedForAR": {
                id: "Periods/IsLockedForAR",
                textAlign: TextAlign.Center,
                formatter: lockedArFormatter,
                fieldSettings: { disableSort: true }
            }
        },
        title: i18next.t("FiscalYear:Title"),
        hierarchy: "Periods"
    };

    const summary: ISummaryItem[] = [
        {
            id: "Number",
            isReadOnly: (args: IGetValueArgs) => {
                if (args.data.StatusCode === FiscalYearStatusCode.NotUsed) {
                    const items = getFiscalYearNumberItems(args.storage as FormStorage);
                    return items.length === 1;
                }
                return true;
            },
            renderEdit: (args: ISummaryItemEditProps) => {
                const onChange = (event: ISmartFieldChange) => {
                    args.storage.handleTemporalChange(event);
                    args.storage.refreshFields();
                };

                const onClose = () => {
                    args.storage.cancelFields([args.bindingContext]);
                    args.onClose();
                };

                const onConfirm = async () => {
                    if (!args.storage.getValue(args.bindingContext)) {
                        args.storage.cancelFields([args.bindingContext]);
                        args.onClose();
                        return;
                    }

                    await args.storage.confirmFields([args.bindingContext]);

                    const number = args.storage.getValue(args.bindingContext);
                    updatePeriodNumbers(args.storage, number);

                    args.storage.refreshFields();
                    args.onClose();
                };

                const items = getFiscalYearNumberItems(args.storage);

                return (
                    <Dialog title={args.storage.t("FiscalYear:Form.ChangeNumber")}
                            onClose={onClose}
                            onConfirm={onConfirm}
                            footer={<ConfirmationButtons onConfirm={onConfirm} onCancel={onClose} useWrapper={false}/>}>
                        <SmartField bindingContext={args.bindingContext}
                                    storage={args.storage}
                                    onTemporalChange={onChange}
                                    fieldDef={{
                                        isConfirmable: true,
                                        type: FieldType.ComboBox,
                                        fieldSettings: {
                                            items
                                        }
                                    }}/>
                    </Dialog>
                );
            }
        },
        {
            id: "Status", label: i18next.t("FiscalYear:Columns.Status"),
            formatter: (value: TValue, args: IFormatOptions) => {
                return (value as IFiscalYearStatusEntity)?.Name;
            },
            colorFormatter: (value: TValue, args: IFormatOptions) => {
                return getStatusColor((value as IFiscalYearStatusEntity)?.Code);
            }
        }
    ];

    const form: IFormDef = {
        id: `${EntityTypeName.FiscalYear}Form`,
        translationFiles: getDefinitions.translationFiles,
        summary,
        isDeletable: true,
        getItemBreadCrumbText: (storage: Model) =>
            getItemBreadCrumbsText(storage, i18next.t("FiscalYear:NewPeriod"),
                storage.data.entity?.Name ?? storage.data.entity?.Number),
        formControl: FiscalYearFormView,
        isReadOnly: isReadOnlyLoose,

        // we need to load ChartOfAccounts and Company to perform check for activate button
        additionalProperties: [
            { id: FiscalYearEntity.ChartOfAccounts },
            { id: FiscalYearEntity.Company },
            { id: FiscalYearEntity.StatusCode },
            {
                id: FiscalYearEntity.Periods,
                additionalProperties: [
                    { id: FiscalPeriodEntity.Number },
                    { id: FiscalPeriodEntity.IsLockedForAP },
                    { id: FiscalPeriodEntity.IsLockedForAR }
                ]
            },
            { id: "Status/Name" }],
        fieldDefinition: {
            StatusCode: {
                customizationData: {
                    useForCustomization: false
                },
                defaultValue: FiscalYearStatusCode.NotUsed,
                // present only for saving purposes, not supposed to be edited
                isVisible: false,
                clearIfInvisible: false
            },
            DateStart: {
                type: FieldType.MonthYear,
                defaultValue: () => {
                    return getDefaultFiscalYearDateStart();
                }
            },
            DateEnd: {
                type: FieldType.MonthYear
            },
            [DefaultPeriodsPath]: {
                backendPath: FiscalYearEntity.Periods,
                type: FieldType.SegmentedButton,
                isRequired: true,
                label: i18next.t("FiscalYear:Form.Period"),
                labelStatus: LabelStatus.Removed,
                auditTrailMode: AuditTrailFieldMode.Hidden,
                customizationData: {
                    useForCustomization: false
                },
                fieldSettings: {
                    items: [
                        {
                            id: FiscalYearDefaultPeriod.Monthly,
                            label: i18next.t("FiscalYear:DefaultPeriods.Monthly")
                        },
                        {
                            id: FiscalYearDefaultPeriod.Quarterly,
                            label: i18next.t("FiscalYear:DefaultPeriods.Quarterly")
                        },
                        {
                            id: FiscalYearDefaultPeriod.Custom,
                            label: i18next.t("FiscalYear:DefaultPeriods.Custom")
                        }
                    ]
                }
            },
            [AddButtonPath]: {
                type: FieldType.Custom,
                isVisible: (args) => {
                    return args.storage.data.entity?.StatusCode === FiscalYearStatusCode.NotUsed && isCustomPeriod(args);
                },
                customizationData: {
                    useForCustomization: false
                },
                renderSkeleton: () => {
                    return <Button isTransparent isSkeleton/>;
                },
                render: (args: IFieldDefFn) => {
                    return <Button isTransparent
                                   icon={<PlusIcon/>}
                                   isDisabled={args.storage.isDisabled}
                                   onClick={handleAddPeriod.bind(null, args.storage as FormStorage)}>{i18next.t("Common:General.Add")}
                    </Button>;
                }
            },

            "Periods/Name": {
                isReadOnly: isReadOnlyLoose,
                isVisible: isCustomPeriod,
                customizationData: {
                    useForCustomization: false
                }
            },
            "Periods/DateStart": {
                type: FieldType.MonthYear,
                isReadOnly: not(isCustomPeriod),
                customizationData: {
                    useForCustomization: false
                }
            },
            "Periods/DateEnd": {
                type: FieldType.MonthYear,
                isReadOnly: not(isCustomPeriod),
                customizationData: {
                    useForCustomization: false
                }
            },
            "Periods/IsLockedForAP": {
                type: FieldType.Checkbox,
                fieldSettings: {
                    checkBoxFieldLabel: i18next.t("FiscalYear:Form.Lock")
                },
                isRequired: false,
                isVisible: (args) => {
                    return !args.storage.context.getAddingNewCompany();
                },
                isReadOnly: isReadOnlyLoose,
                groupedField: GroupedField.MultiStart,
                customizationData: {
                    useForCustomization: false
                }
            },
            "Periods/IsLockedForAR": {
                type: FieldType.Checkbox,
                labelStatus: LabelStatus.Hidden,
                isRequired: false,
                isVisible: (args) => {
                    return !args.storage.context.getAddingNewCompany();
                },
                isReadOnly: isReadOnlyLoose,
                groupedField: GroupedField.MultiEnd,
                customizationData: {
                    useForCustomization: false
                }
            }
        },
        groups: [
            {
                id: "basic",
                rows: [
                    [{ id: FiscalYearEntity.DateStart }, { id: FiscalYearEntity.DateEnd }]
                ]
            },
            {
                id: "defaultPeriods",
                title: i18next.t("FiscalYear:Form.Periods"),
                customizationData: {
                    useForCustomization: false
                },
                rows: [
                    [{ id: DefaultPeriodsPath }]
                ]
            },
            {
                id: "Periods",
                collection: FiscalYearEntity.Periods,
                collectionOrder: FiscalPeriodEntity.DateStart,
                showGroupDivider: false,
                customizationData: {
                    useForCustomization: false
                },
                isVisible: (args) => {
                    return !args.storage.context.getAddingNewCompany() || isCustomPeriod(args);
                },
                isRemovable: (args) => {
                    return args.storage.data.entity?.StatusCode === FiscalYearStatusCode.NotUsed && isCustomPeriod(args);
                },
                // use common title because of customization
                title: i18next.t("FiscalYear:Form.Period"),

                collectionItemTitle: (index: number, item: IEntity) => {
                    const period = item as IFiscalPeriodEntity;
                    return `${index + 1}. ${i18next.t("FiscalYear:Form.Period")} - ${period.Number}`;
                },
                rows: [
                    [{ id: FiscalPeriodEntity.Name }, { id: FiscalPeriodEntity.DateStart }, { id: FiscalPeriodEntity.DateEnd }]
                ],
                afterContent: [{ id: FiscalPeriodEntity.IsLockedForAP }, { id: FiscalPeriodEntity.IsLockedForAR }]
            },
            {
                id: "actions",
                customizationData: {
                    useForCustomization: false
                },
                rows: [
                    [{ id: AddButtonPath }]
                ]
            }
        ]
    };

    return {
        entitySet: EntitySetName.FiscalYears,
        table,
        form
    };
};

getDefinitions.translationFiles = ["FiscalYear"];
setDefByEntityType(EntityTypeName.FiscalYear, getDefinitions);